import { useSeo } from '../lib/seo';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowDown, ArrowLeft, ArrowUpRight, Globe, Mail, MapPin, Phone, Star, Trophy } from 'lucide-react';
import api from '../lib/api';
import { track } from '../lib/tracker';
import AttractionCard from '../components/AttractionCard';
import { AccessibilityPanel } from '../components/Accessibility';

// If this venue is a StayPlayExplore partner — hotel or attraction — show a
// short paragraph + cross-link so visitors can jump straight to the offer.
function StayPlayExploreCrossLink({ a }) {
  const [pkg, setPkg] = useState(null);
  useEffect(() => {
    if (!a?.name || (!a.stayplayexplore && (a.category || '') !== 'accommodation')) return;
    api.get('/packages').then((r) => {
      const items = r.data.items || r.data || [];
      if (a.stayplayexplore) {
        // explicitly flagged as an SPE partner — link the SPE package itself
        const spe = items.find((p) => `${p.slug || ''} ${p.title || ''}`.toLowerCase().includes('stayplayexplore')) || items[0];
        setPkg(spe || null);
        return;
      }
      const name = a.name.toLowerCase();
      const hit = items.find((p) =>
        `${p.title || ''} ${p.subtitle || ''} ${p.description || ''} ${(p.includes || []).join(' ')}`.toLowerCase().includes(name)
      );
      setPkg(hit || null);
    }).catch(() => {});
  }, [a]);
  if (!pkg) return null;
  const isStay = (a.category || '') === 'accommodation';
  return (
    <div className="mt-10 bg-mustard/15 border border-mustard/50 rounded-3xl p-6 md:p-8" data-testid="spe-crosslink">
      <div className="label text-heritage mb-2">StayPlayExplore</div>
      <h3 className="font-display text-xl md:text-2xl mb-2">{isStay ? 'Stay here as part of a StayPlayExplore break' : 'Visit here as part of a StayPlayExplore break'}</h3>
      <p className="text-charcoal/80 text-sm md:text-[1rem] leading-relaxed mb-4">
        {a.name} features in <strong>{pkg.title}</strong> — an overnight stay bundled with attraction tickets{isStay ? '' : ' (including this one)'}, so you can stay, play and explore Leicestershire in one booking{pkg.price_from ? ` (from £${String(pkg.price_from).replace(/^£/, '')})` : ''}.
      </p>
      <Link to="/packages" data-testid="spe-crosslink-btn" className="inline-flex items-center gap-2 bg-charcoal text-base px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-charcoal/90">
        See the StayPlayExplore breaks <ArrowUpRight size={14}/>
      </Link>
    </div>
  );
}

// If this venue is a Perfect Break (adults-only) partner — hotel or experience —
// show a branded panel linking to theperfectbreak.co.uk.
function PerfectBreakCrossLink({ a }) {
  const [pkg, setPkg] = useState(null);
  useEffect(() => {
    if (!a?.perfectbreak) return;
    api.get('/packages').then((r) => {
      const items = r.data.items || r.data || [];
      const hit = items.find((p) => `${p.slug || ''} ${p.title || ''}`.toLowerCase().includes('perfect break') || (p.slug || '').includes('perfect-break'));
      setPkg(hit || null);
    }).catch(() => setPkg({}));
  }, [a]);
  if (!a?.perfectbreak) return null;
  const isStay = (a.category || '') === 'accommodation';
  return (
    <div className="mt-10 bg-heritage/10 border border-heritage/40 rounded-3xl p-6 md:p-8" data-testid="perfectbreak-crosslink">
      <div className="label text-heritage mb-2">The Perfect Break · Adults only</div>
      <h3 className="font-display text-xl md:text-2xl mb-2">{isStay ? 'Stay here as part of an adults-only Perfect Break' : 'Enjoy this as part of an adults-only Perfect Break'}</h3>
      <p className="text-charcoal/80 text-sm md:text-[1rem] leading-relaxed mb-4">
        {a.name} is a partner {isStay ? 'hotel' : 'experience'} in <strong>The Perfect Break</strong> — fixed-price adults-only short breaks from £169 for two, bundling a hotel stay, breakfast and up to three hand-picked experiences{isStay ? '' : ' (including this one)'}.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <a href="https://www.theperfectbreak.co.uk/" target="_blank" rel="noopener noreferrer" data-testid="perfectbreak-crosslink-btn" className="inline-flex items-center gap-2 bg-heritage text-base px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-heritage/90">
          Book at theperfectbreak.co.uk <ArrowUpRight size={14}/>
        </a>
        {pkg && pkg.slug && (
          <Link to="/packages" data-testid="perfectbreak-crosslink-more" className="inline-flex items-center gap-1.5 text-heritage font-semibold text-sm hover:underline">
            See break details
          </Link>
        )}
      </div>
    </div>
  );
}

export default function AttractionDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  useSeo(
    a ? `${a.name}${a.town ? `, ${a.town}` : ''} — Visitor Info | Go Leicestershire` : null,
    a?.summary || a?.description?.slice(0, 160)
  );
  const [schema, setSchema] = useState(null);
  const [err, setErr] = useState(false);
  const viewTracked = useRef(null);

  useEffect(() => {
    api.get(`/attractions/${slug}`).then((r) => setA(r.data)).catch(() => setErr(true));
    api.get(`/schema/attraction/${slug}`).then((r) => setSchema(r.data)).catch(() => {});
    // Track view once per slug (StrictMode safe — useRef sentinel prevents double-fire in dev)
    if (viewTracked.current !== slug) {
      viewTracked.current = slug;
      api.post(`/attractions/${slug}/view`).catch(() => {});
      track.attractionView(slug);
    }
    window.scrollTo(0, 0);
  }, [slug]);

  if (err) return <div className="max-w-[1100px] mx-auto px-6 py-32 text-center"><Helmet><title>Attraction not found · GoLeicestershire</title><meta name="robots" content="noindex" /></Helmet><h1 className="font-display text-4xl">Attraction not found</h1><Link to="/explore" className="text-heritage mt-4 inline-block">← Back to Explore</Link></div>;
  if (!a) return <div className="py-32 text-center text-muted">Loading...</div>;

  const tripUrl = a.tripadvisor_url || `https://www.tripadvisor.com/Search?q=${encodeURIComponent(a.name + ' Leicestershire')}`;

  return (
    <article>
      <Helmet>
        <title>{`${a.name} · GoLeicestershire`}</title>
        <meta name="description" content={a.summary || a.description?.slice(0, 160)}/>
        <meta property="og:title" content={a.name}/>
        <meta property="og:description" content={a.summary || a.description?.slice(0, 160)}/>
        {a.hero_image && <meta property="og:image" content={a.hero_image}/>}
        <meta property="og:type" content="article"/>
        {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
      </Helmet>
      {/* HERO */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-8 pb-16">
        <Link to="/explore" className="inline-flex items-center gap-2 text-muted hover:text-heritage mb-8" data-testid="back-link"><ArrowLeft size={16}/> All attractions</Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden">
              <img src={a.hero_image} alt={a.name} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-5 flex flex-col">
            <div className="label text-heritage mb-3">{a.category?.replace(/_/g, ' ')}</div>
            <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] mb-6">{a.name}</h1>
            {a.nominee_badge && (!a.nominee_badge_until || new Date(a.nominee_badge_until + 'T23:59:59') >= new Date()) && (
              <span data-testid="detail-nominee-badge" className="inline-flex items-center gap-1.5 bg-forest text-[#FAF8F5] text-xs font-semibold px-3 py-1.5 rounded-full mb-6 -mt-2">
                {a.nominee_badge}
              </span>
            )}
            {a.awards_won && a.awards_won.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="detail-awards">
                {a.awards_won.map((aw, i) => (
                  aw.status === 'historic' ? (
                    <span key={i} className="inline-flex items-center gap-1.5 bg-mustard text-charcoal text-sm font-semibold px-4 py-2 rounded-full">
                      <Trophy size={14}/> {aw.category} {aw.year}
                    </span>
                  ) : (
                    <Link key={i} to={`/awards/${aw.year}`} title="See all winners from this year"
                      className="inline-flex items-center gap-1.5 bg-mustard text-charcoal text-sm font-semibold px-4 py-2 rounded-full hover:bg-heritage hover:text-base transition-colors">
                      <Trophy size={14}/> {aw.category} {aw.year}
                    </Link>
                  )
                ))}
              </div>
            )}
            {a.town && <div className="flex items-center gap-2 text-muted text-lg mb-6"><MapPin size={18} className="text-heritage" /> {a.town}{a.address ? ` · ${a.address.split(',')[0]}` : ''}</div>}
            <p className="text-lg text-charcoal/85 leading-relaxed mb-3" data-testid="detail-summary">{a.summary || a.description?.slice(0, 280)}</p>
            {(a.description && a.description.trim() && a.description.trim() !== (a.summary || '').trim()) && (
              <a href="#detail-about" data-testid="detail-readmore" className="inline-flex items-center gap-1.5 text-heritage font-semibold text-sm hover:text-charcoal mb-8 group">
                Read more about {a.name}
                <ArrowDown size={14} className="transition-transform group-hover:translate-y-0.5" />
              </a>
            )}
            <div className="flex flex-wrap gap-2 mt-5 mb-8">
              {a.tags?.map((t) => <span key={t} className="bg-stone text-charcoal px-4 py-2 rounded-full text-sm font-medium">{t}</span>)}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {a.family_friendly && <div className="bg-base border border-charcoal/10 rounded-2xl p-4"><div className="label text-forest">Family</div><div className="mt-1">Yes</div></div>}
              {a.dog_friendly && <div className="bg-base border border-charcoal/10 rounded-2xl p-4"><div className="label text-forest">Dogs</div><div className="mt-1">Welcome</div></div>}
              {a.free_entry && <div className="bg-base border border-charcoal/10 rounded-2xl p-4"><div className="label text-forest">Entry</div><div className="mt-1">Free</div></div>}
              {a.accessible && <div className="bg-base border border-charcoal/10 rounded-2xl p-4"><div className="label text-forest">Access</div><div className="mt-1">Accessible</div></div>}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {a.website && (
                <a href={a.website} target="_blank" rel="noreferrer" data-testid="cta-website" className="bg-heritage text-base px-6 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 hover:scale-[1.03] transition-transform">
                  Visit official website <ArrowUpRight size={16}/>
                </a>
              )}
              <a href={tripUrl} target="_blank" rel="noreferrer" data-testid="cta-tripadvisor" className="border border-charcoal/20 px-6 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 hover:border-charcoal transition-colors">
                <Star size={16}/> TripAdvisor reviews
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* BODY + CONTACT */}
      <div id="detail-about" className="bg-stone/40 py-20 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="label text-heritage mb-4">About {a.name}</div>
            <div className="text-lg leading-relaxed text-charcoal/85 whitespace-pre-wrap" data-testid="detail-description">
              {a.description}
            </div>
            <StayPlayExploreCrossLink a={a} />
            <PerfectBreakCrossLink a={a} />
            {a.gallery?.length > 1 && (
              <div className="mt-12">
                <div className="label text-heritage mb-4">Gallery</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {a.gallery.slice(0, 6).map((src, i) => (
                    <div key={i} className="aspect-[4/3] overflow-hidden rounded-2xl">
                      <img
                        src={src}
                        alt={`${a.name} — gallery image ${i + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Accessibility panel — only renders if there's at least one
                confirmed yes/no, a note, or an external guide URL. Hidden
                otherwise so empty venues don't show an empty panel. */}
            <div className="mt-12">
              <AccessibilityPanel accessibility={a.accessibility} venueName={a.name} />
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="bg-white rounded-3xl border border-charcoal/10 p-8 sticky top-28">
              <div className="label text-heritage mb-4">Contact</div>
              <ul className="space-y-4">
                {a.address && (
                  <li className="flex items-start gap-3">
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.address)}`} target="_blank" rel="noreferrer" data-testid="attraction-address-link" className="flex items-start gap-3 text-sm leading-relaxed hover:text-heritage underline-offset-2 hover:underline" title="Open in Google Maps">
                      <MapPin size={18} className="text-forest mt-0.5 shrink-0"/>
                      <span>{a.address}</span>
                    </a>
                  </li>
                )}
                {a.phone && <li className="flex items-center gap-3"><Phone size={18} className="text-forest shrink-0"/><a href={`tel:${a.phone}`} className="text-sm hover:text-heritage">{a.phone}</a></li>}
                {a.email && <li className="flex items-center gap-3"><Mail size={18} className="text-forest shrink-0"/><a href={`mailto:${a.email}`} className="text-sm hover:text-heritage break-all">{a.email}</a></li>}
                {a.website && <li className="flex items-center gap-3"><Globe size={18} className="text-forest shrink-0"/><a href={a.website} target="_blank" rel="noreferrer" className="text-sm hover:text-heritage break-all">{a.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a></li>}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* RELATED */}
      {a.related?.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
          <h2 className="font-display text-3xl lg:text-4xl mb-10">More like this</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {a.related.map((r) => <AttractionCard key={r.id} a={r}/>)}
          </div>
        </div>
      )}

      {/* NEARBY ONWARD JOURNEYS */}
      {(a.nearby_events?.length > 0 || a.nearby_food?.length > 0 || a.nearby_stays?.length > 0) && (
        <div className="bg-stone/40 py-16" data-testid="nearby-section">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 space-y-14">
            {a.nearby_events?.length > 0 && (
              <div data-testid="nearby-events">
                <div className="flex items-baseline justify-between mb-6">
                  <h2 className="font-display text-3xl">What's on {a.town ? `in ${a.town}` : 'nearby'}</h2>
                  <Link to="/events" className="text-heritage font-semibold text-sm hover:underline">All events →</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {a.nearby_events.map((ev) => (
                    <Link key={ev.id} to={ev.slug ? `/events/${ev.slug}` : '/events'} data-testid="nearby-event-card"
                      className="bg-white border border-charcoal/10 rounded-2xl p-5 hover:border-heritage transition-colors group">
                      <div className="label text-heritage mb-2">{ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ''}</div>
                      <div className="font-display text-lg leading-snug group-hover:text-heritage transition-colors">{ev.title}</div>
                      {ev.venue && <div className="text-muted text-sm mt-1.5">{ev.venue}</div>}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {a.nearby_food?.length > 0 && (
              <div data-testid="nearby-food">
                <h2 className="font-display text-3xl mb-6">Places to eat & drink {a.town ? `in ${a.town}` : 'nearby'}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {a.nearby_food.map((r) => <AttractionCard key={r.id} a={r}/>)}
                </div>
              </div>
            )}
            {a.nearby_stays?.length > 0 && (
              <div data-testid="nearby-stays">
                <div className="flex items-baseline justify-between mb-6">
                  <h2 className="font-display text-3xl">Stay {a.town ? `in ${a.town}` : 'nearby'}</h2>
                  <Link to="/accommodation" className="text-heritage font-semibold text-sm hover:underline">All places to stay →</Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {a.nearby_stays.map((r) => <AttractionCard key={r.id} a={r}/>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
